import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import { Box } from '../Box/Box';
import { ShadowOverlay } from '../ShadowEffect/ShadowEffect';
const TriggerWrapper = /*#__PURE__*/styled(Box).withConfig({
  displayName: "Popout__TriggerWrapper",
  componentId: "sc-hnx2ed-0"
})(["position:relative;width:fit-content;"]);
const OnClickHandler = /*#__PURE__*/styled(Box).withConfig({
  displayName: "Popout__OnClickHandler",
  componentId: "sc-hnx2ed-1"
})(["position:absolute;top:0;width:100%;height:100%;cursor:pointer;"]);
const PopoutModal = /*#__PURE__*/styled(Box).withConfig({
  displayName: "Popout__PopoutModal",
  componentId: "sc-hnx2ed-2"
})(["position:absolute;z-index:2;transition:padding 200ms ease-in-out,margin 200ms ease-in-out,border-radius 200ms ease-in-out;"]);
/**
 * @public
 */
export function Popout(props) {
  const {
    trigger,
    content,
    onOpen,
    onClose,
    triggerRef,
    closeOnTriggerRefClick
  } = props;
  const [isOpen, setIsOpen] = useState(false);
  const [height, setHeight] = useState(0);
  const [padding, setPadding] = useState(0);
  const [opacity, setOpacity] = useState(0);
  const baseInputRef = useRef(null);
  const modalRef = useRef(null);
  const isMounted = useRef(false);
  const handleOpen = useCallback(() => setIsOpen(true), []);
  const handleClose = useCallback(() => setIsOpen(false), []);
  useEffect(() => {
    if (isMounted.current) {
      if (isOpen) {
        setPadding(16);
        setOpacity(0.5);
        /* istanbul ignore next */
        triggerRef?.current?.focus();
        /* istanbul ignore next */
        onOpen?.();
      } else {
        setPadding(0);
        setOpacity(0);
        /* istanbul ignore next */
        onClose?.();
      }
      return;
    }
    isMounted.current = true;
  }, [isOpen, triggerRef]);
  useLayoutEffect(() => {
    setHeight(baseInputRef.current.clientHeight);
  }, [trigger]);
  useEffect(() => {
    if (isOpen && closeOnTriggerRefClick) {
      triggerRef?.current?.addEventListener('click', handleClose);
    }
    return () => triggerRef?.current?.removeEventListener('click', handleClose);
  }, [isOpen, closeOnTriggerRefClick, triggerRef, handleClose]);
  useEffect(() => {
    const escapeListener = e => {
      if (isOpen && e.key === 'Escape') {
        handleClose();
      }
    };
    const focusListener = e => {
      if (isOpen && e.target instanceof HTMLElement && e.target !== baseInputRef.current && !modalRef.current.contains(e.target)) {
        handleClose();
      }
    };
    document.addEventListener('focusin', focusListener);
    document.addEventListener('keydown', escapeListener);
    return () => {
      document.removeEventListener('focusin', focusListener);
      document.removeEventListener('keydown', escapeListener);
    };
  }, [isOpen, handleClose]);
  const placeHolderDiv = React.createElement(Box, {
    style: {
      height
    }
  });
  return React.createElement(Box, null, React.createElement(TriggerWrapper, {
    tabIndex: isOpen ? -1 : 0,
    onFocus: handleOpen,
    ref: baseInputRef
  }, isOpen ? placeHolderDiv : trigger, React.createElement(OnClickHandler, {
    onClick: handleOpen
  })), isOpen && React.createElement(React.Fragment, null, React.createElement(ShadowOverlay, {
    zIndex: 1,
    onClick: handleClose,
    style: {
      opacity,
      transition: 'opacity 250ms ease-in-out'
    }
  }), React.createElement(PopoutModal, {
    ref: modalRef,
    boxShadowSize: 'overlay-lg',
    bg: 'background.lightest',
    style: {
      padding: padding,
      borderRadius: padding,
      marginTop: -(height + padding),
      marginLeft: -padding
    }
  }, trigger, content)));
}